import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  employeesService,
  type EmployeeListableType,
} from '@/features/employees/services/employees.service';
import { EmployeeList } from '@/features/employees/components/EmployeeList';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { History } from 'lucide-react';
import { PageHeader } from '@/components/ui/page-header';
import { optStr, optPage, optEnum } from '@/lib/list-search';

const ACTIVE_VALUES = ['active', 'inactive', 'all'] as const;
const TYPE_VALUES = ['pj', 'clt', 'estagio', 'socio'] as const;
const SORT_VALUES = ['name-asc', 'cnpj-with-first', 'cnpj-without-first'] as const;

export interface EmployeesSearch {
  search?: string;
  status?: (typeof ACTIVE_VALUES)[number];
  type?: EmployeeListableType;
  sort?: (typeof SORT_VALUES)[number];
  page?: number;
}

// valores padrão (status=active, sort=name-asc, página 1) ficam fora da URL
export const validateEmployeesSearch = (s: Record<string, unknown>): EmployeesSearch => ({
  search: optStr(s.search),
  status: optEnum(s.status, ACTIVE_VALUES),
  type: optEnum(s.type, TYPE_VALUES) as EmployeeListableType | undefined,
  sort: optEnum(s.sort, SORT_VALUES),
  page: optPage(s.page),
});

export const Route = createFileRoute('/_authenticated/employees/')({
  validateSearch: validateEmployeesSearch,
  component: EmployeesPage,
});

const PAGE_SIZE = 20;

type SortOption = 'name-asc' | 'cnpj-with-first' | 'cnpj-without-first';

const SORT_PARAMS: Record<
  SortOption,
  { sortBy: 'name' | 'hasCnpj'; sortOrder: 'asc' | 'desc' }
> = {
  'name-asc': { sortBy: 'name', sortOrder: 'asc' },
  'cnpj-with-first': { sortBy: 'hasCnpj', sortOrder: 'asc' },
  'cnpj-without-first': { sortBy: 'hasCnpj', sortOrder: 'desc' },
};

function EmployeesPage() {
  const navigate = useNavigate();
  const urlSearch = Route.useSearch();
  const searchNavigate = Route.useNavigate();
  const activeFilter = urlSearch.status ?? 'active';
  const typeFilter = urlSearch.type ?? 'all';
  const sort: SortOption = urlSearch.sort ?? 'name-asc';
  const search = urlSearch.search ?? '';
  const page = urlSearch.page ?? 1;

  const setParams = useCallback(
    (patch: Partial<EmployeesSearch>) =>
      searchNavigate({
        search: (prev) => ({ ...prev, page: undefined, ...patch }),
        replace: true,
      }),
    [searchNavigate],
  );

  const handleSearch = useCallback(
    (value: string) => {
      if (value.trim() !== search) setParams({ search: value.trim() || undefined });
    },
    [search, setParams],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['employees-list', activeFilter, typeFilter, sort, search, page],
    queryFn: () =>
      employeesService.listEmployees({
        active:
          activeFilter === 'active'
            ? true
            : activeFilter === 'inactive'
              ? false
              : undefined,
        employmentType: typeFilter === 'all' ? undefined : typeFilter,
        search: search || undefined,
        ...SORT_PARAMS[sort],
        page,
        limit: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const employees = data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Prestadores"
        description="Lista de prestadores com tipo de contratação definido."
        actions={
          <Button
            variant="outline"
            onClick={() =>
              navigate({
                to: '/employees/logs',
                state: { from: '/employees', fromLabel: 'Prestadores' },
              })
            }
          >
            <History className="mr-2 h-4 w-4" />
            Histórico
          </Button>
        }
      />

      <Card>
        <CardHeader className="flex flex-col gap-4">
          <CardTitle>Lista de Prestadores</CardTitle>
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="w-full md:max-w-sm">
              <SearchInput
                placeholder="Buscar por nome, e-mail ou CNPJ"
                value={search}
                onSearch={handleSearch}
              />
            </div>
            <Select
              value={typeFilter}
              onValueChange={(v) =>
                setParams({ type: v === 'all' ? undefined : (v as EmployeeListableType) })
              }
            >
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="pj">PJ</SelectItem>
                <SelectItem value="clt">CLT</SelectItem>
                <SelectItem value="estagio">Estágio</SelectItem>
                <SelectItem value="socio">Sócio</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={activeFilter}
              onValueChange={(v) =>
                setParams({ status: v === 'active' ? undefined : (v as EmployeesSearch['status']) })
              }
            >
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="inactive">Inativos</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={sort}
              onValueChange={(v) =>
                setParams({ sort: v === 'name-asc' ? undefined : (v as SortOption) })
              }
            >
              <SelectTrigger className="w-full md:w-56">
                <SelectValue placeholder="Ordenar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name-asc">Nome (A–Z)</SelectItem>
                <SelectItem value="cnpj-with-first">Com CNPJ primeiro</SelectItem>
                <SelectItem value="cnpj-without-first">Sem CNPJ primeiro</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <EmployeeList
            employees={employees}
            isLoading={isLoading}
            onSelect={(id) => navigate({ to: `/employees/${id}` })}
          />
          {data && (
            <Pagination
              total={data.total}
              page={data.page}
              limit={data.limit}
              onPageChange={(p) => setParams({ page: p > 1 ? p : undefined })}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
